import _ from "underscore";
import Backbone, {ViewOptions} from "backbone";
import Model = Backbone.Model;
import {dispatcher, DispatcherEvents} from "./utils/dispatcher";
import LockScreenView from "./ui/hos/lock";
import storage, { StorageKeys } from "./utils/storage/storage";
import {sanitizeUserName, UserService} from "./utils/users";
import {Device} from "./model/Device";
import {Router} from "./router";
import Api from "./services/api";
import AppManager, {AppManagerEvents} from "./app.manager";
import Sync from "./sync/sync";
import {DatabaseCache} from "./services/databaseCache";
import VehicleService from "./services/vehicle";
import TranslationService from "./services/translation";
import IoxDataHandler from "./services/ioxDataHandler";
import Network from "./services/network";
import Session from "./utils/session";
import TopbarView from "./ui/topbar";
import MenuView from "./ui/menu";
import HosProcess from "./services/hosProcess";
import {getCurrentVehicle, isNoVehicle} from "./utils/device";
import DriveView, { TDriveViewConstructor, IDriveViewOptions } from "./DriveView";
import AddinView from "./ui/addin";
import WorkflowView, {IWorkflowState} from "./ui/workflow";
import LoginView from "./ui/login";
import { Dialog, removeDialog, applyDriverActionNecessary, IDialogButton } from "./ui/framework/dialog";
import {getServerDate} from "./utils/storage/date";
import idSerializer from "geotab-uuid";
import Context from "./app.context";
import {DutyStatusLogService} from "./model/DutyStatusLogs";
import {isDriverViewingInspectionMode, isDriverViewingDvirMode, isDriverOnAnyWorkflow} from "./utils/uri";
import {getUnverifiedZoneCycleLogs, shouldShowZoneCycleDialog} from "./ui/framework/utils/zoneCycleChangeHelper";
import {BigSpinner} from "./utils/spinner";
import { htmlEscape, show, hide } from "./utils/vanilla";
import { changeTimeZoneForDstCalculator } from "./utils/date";
import { time, timeEnd as logTimeEnd, error as logError, event as logEvent, log, feedback } from "./utils/logger.internal";
import { Source, Event as LoggerEvent, log as loggerLog, AdditionalDetails } from "./utils/logger";
import {AddinService} from "./services/addin";
import {AddinManager} from "./services/addinManager";
import {CancelablePromise} from "./utils/cancelablePromise";
import {handleApi, IExtendedError} from "./utils/error";
import DutyStatusLog from "./model/DutyStatusLog";
import {webViewLayer} from "./utils/webViewLayer";
import ScreenHandler from "./services/screenHandler";
import {DriveStore, GlobalReducer} from "./store/reducers";
import {observeStore} from "./utils/store";
import {MobileActions} from "./store/mobile/actions";
import mainScheduler from "./utils/scheduler";
import { WorkdayType, IoxActionTypes, IFeatureFlagStatus } from "./store/data/types";
import {DataActions} from "./store/data/actions";
import Alerts from "./services/alerts";
import {VehicleEvents} from "./ui/vehicleEvents";
import toastr, {ToastOrigins} from "./utils/toastr";
import News from "./services/news";
import {ServiceWorkerEvents} from "./ui/serviceWorkerEvents";
import PictureViewDialog from "./ui/framework/pictureViewDialog";
import startLoggingChanges from "./services/storeChangeLogger";
import {SecurityService} from "./utils/security";
import {LocalizerService} from "./utils/localizer";
import {ErrorHandler} from "./utils/error.impl";
import {startAppCacheListeners} from "./utils/appcache.listeners";
import ZoneCycleChangeDialog from "./ui/framework/zoneCycleChangeDialog";
import appHTML from "./app.view.tmpl";
import { isInCanada, type THosMalfunction, type THosOrigin, type THosStatus } from "hos-tools";
import {redirectDatabaseIfNeeded} from "./utils/redirectioner";
import constants from "./constants";
import {IBackground, IDriverActionNecessary, ILoginRequiredState, NetworkStates, IMobilePageState} from "./store/mobile/types";
import {updateAppCache} from "./utils/appcache";
import serviceWorker from "./utils/serviceWorker";
import {UserHosRuleSetService} from "./model/UserHosRuleSets";
import {keepScreenOn, setPipMode} from "./utils/native/nativeHelpers";
import GTMService from "./services/gtmService";
import {getDriveDebug, IDriveDebug} from "./utils/debug/driveDebug";
import {FeatureFlagName} from "./utils/featureFlagName";
import {DVIRLogsService} from "./model/DVIRLogsService";
import {FeatureFlags, IFeatureFlagsService} from "./services/featureFlags";
import {makeDeviceCache} from "./utils/storage/cache";
import { clearLegacyLogoutStorage } from "./utils/legacyLogoutStorage";
import {IApiError} from "./utils/api.interfaces";
import DebugPageView from "./ui/debug/debugPageView";
import { TranslationActions } from "./store/translation/actions";
import { ErrorTracker } from "./utils/errorHandler/errorTracker";
import { isAllowThirdPartyLoggingEnabled } from "./utils/serverFlags";
import DriverChangeLogs from "./model/DriverChangeLogs";
import { CacheActions } from "./store/cache/actions";
import { ICache } from "./store/cache/types";
import { UserUpdater } from "./utils/userUpdater";
import { isDriverContinuingPC, isPcExemptedPeriod, isThePromptNotExpected } from "./ui/framework/utils/vehicleEndedStatusLogic";
import { collectDashboardAddins, executeStartupAddins } from "./utils/addinHelper";
import DashboardView from "./ui/main";
import IndexedDbMigrationFromRedux from "./utils/storage/indexedDbMigrationFromRedux";
import { IndexedDbStorage } from "./utils/storage/indexedDbStorage";
import { DRIVE_STORAGE_PREFIX } from "./store/storageConstants";
import { DriveNativeStatePublisher } from "./utils/native/driveNativeStatePublisher";

const SCROLL_MINIMUM = 500;

export interface IAppView {
    app?: AppView;
}

declare let process: {
    env: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        NODE_ENV: string;
    };
};

export interface IPromptOptions {
    shouldShowPrompt: boolean;
    newStatus?: THosStatus;
    title?: string;
    message?: string;
    isEndedYmPrompt?: boolean | undefined;
    isEndedPcPrompt?: boolean | undefined;
    isEndedPcViaSdc?: boolean | undefined;
    isStoppedDrivingPrompt?: boolean | undefined;
    selfClosingTimeLimit?: number | undefined;
}

export interface IVehicleEndedStatusOptions {
    sdcEndedPc?: boolean;
    onPromptPresented?: () => void;
    onPromptHandled?: () => void;
    onPromptDismissed?: () => void;
}

export interface IAppViewOptions {
    viewOptions: ViewOptions<Model>;
    store: DriveStore;
    featureFlagService: FeatureFlags;
    userService: UserService;
    window?: Window;
    errorTracker?: ErrorTracker;
}

interface AppCacheDownloadedEvent extends Event {
    detail: number;
}

export default class AppView extends Backbone.NativeView<Model> {
    contentView: DriveView<Backbone.Model> | undefined = undefined;
    debug?: IDriveDebug;
    menuView: MenuView;
    router: Router;
    addinManager: AddinManager | null = null;
    // Dealing with the lock screen and auto-ON/auto-D
    cachedContentView: TDriveViewConstructor<Backbone.Model> | null = null;
    contentViewCacheOptions: IDriveViewOptions<Backbone.Model> | undefined;
    contentViewCachePath: any;
    session: Session;
    store: DriveStore;
    vehicleEvents: VehicleEvents;
    serviceWorkerEvents: ServiceWorkerEvents;
    readonly featureFlagService: IFeatureFlagsService;
    readonly userService: UserService;
    readonly topbarView: TopbarView;
    private readonly basePageTitle = document.title;
    private readonly addinStartupBlockedPages: string[] = ["login", "workflow"];
    private readonly template = _.template(appHTML);
    // eslint-disable-next-line @typescript-eslint/naming-convention
    private readonly _manager: AppManager;
    private readonly gtmService: GTMService;
    private spinner;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    private _context!: Context;
    // needed to avoid race-condition of translating views
    private readonly translating: {[key: string]: DriveView<Backbone.Model>} = {};
    private readonly onScrollBind: any;
    private readonly errorTracker: ErrorTracker | undefined;
    private shouldShowScrollBtn = false;
    private isScrollBtnVisible = false;
    private endedExemptionOnRevokeLogId: string | null = null;
    // A VehicleEndedStatus prompt suppressed because a DVIR was open (MYG-151386). Re-evaluated
    // once the driver leaves the DVIR (see pageLoaded()) so the prompt isn't dropped forever -
    // checkIfPromptShouldBeIgnored() only gates whether a NEW prompt may open; it has no way to
    // retroactively show one once the view changes.
    private pendingDvirPrompt: { slimLog: DutyStatusLog; options?: IVehicleEndedStatusOptions } | null = null;
    private checkingNetworkStatusTask: any;
    private readonly driveNativeStatePublisher: DriveNativeStatePublisher;

    constructor(options: IAppViewOptions) {
        super(options.viewOptions);

        this.store = options.store;
        this.userService = options.userService;

        this.featureFlagService = options.featureFlagService;
        this.errorTracker = options.errorTracker;
        this.driveNativeStatePublisher = new DriveNativeStatePublisher(this.store);
        this.setupStoreSubscribers();
        startLoggingChanges(this.store);
        this.router = new Router(this, this.errorTracker);

        this._manager = new AppManager(this.store, this.router, this.featureFlagService, this.userService);
        this.context = this._manager.getContext();

        this.vehicleEvents = new VehicleEvents(this.store, this.context, this.router, this.dutyStatusLogService).startWatching();
        this.vehicleEvents.replayPendingEvents();
        this.serviceWorkerEvents = new ServiceWorkerEvents(this.context).startWatching();

        this.session = new Session({ app: this });
        this.menuView = new MenuView({ app: this });
        this.topbarView = new TopbarView({ app: this });

        this.gtmService = new GTMService(this.store, options.window || window, document);
        this.gtmService.initialize();
        this.gtmService.collectData();

        if (process.env.NODE_ENV !== "production") {
            this.debug = getDriveDebug(
                (userName?: string) => {
                    if (userName) {
                        const context = this._manager["activeContexts"][userName.toLowerCase()];
                        if (context) {
                            return [context.api];
                        }
                        return [this.api];
                    } else {
                        let apiInstances: Api[] = [], activeContexts = this._manager["activeContexts"];
                        Object.keys(activeContexts).forEach(user => {
                            apiInstances.push(activeContexts[user].api);
                        });
                        return apiInstances;
                    }
                },
                () => this.manager.movement,
                () => this.errorTracker,
                () => this.addinManager || null
            );

            if (window.__GPS_JAMMER_ENABLED__) {
                this.debug.toggleGpsJammer(true);
            }
        }

        if (redirectDatabaseIfNeeded(this.context)) {
            return;
        }
        this.updateCachingMethod();

        // eslint-disable-next-line @typescript-eslint/unbound-method
        this.manager.events.on(AppManagerEvents.ContextRemoved, this.onContextRemoved, this);

        this.spinner = new BigSpinner({
            lines: 11,
            length: 32,
            width: 14,
            speed: 1.5,
            trail: 60,
            top: 150
        }).spin(document.body);

        // Initialize services and event listeners
        this.listenTo(dispatcher, DispatcherEvents.LockscreenToggle, this.lockScreen.bind(this));
        this.listenTo(dispatcher, DispatcherEvents.StatusCharging, (isCharging: boolean | undefined) => {
            // Proxies this to store, because nativeContainer's message can't easily get access to `store`
            this.store.dispatch(MobileActions.setCharging(isCharging));
        });
        this.listenTo(dispatcher, DispatcherEvents.ViewPageloaded, this.pageLoaded.bind(this));
        this.listenTo(dispatcher, DispatcherEvents.UserUpdateLanguage, this.onUserLanguageUpdate.bind(this));
        this.listenTo(dispatcher, DispatcherEvents.SyncHosLocationManual, this.promptDutyStatusAddresses.bind(this));
        this.listenTo(dispatcher, DispatcherEvents.VehicleEndedStatus, this.promptDriverFromLog.bind(this));
        this.listenTo(dispatcher, DispatcherEvents.VehicleChangedInExemption, this.showContinueExemptionDialogOrExitExemptionForDriver.bind(this));
        this.listenTo(dispatcher, DispatcherEvents.ApiNeedsCredentials, this.needsCredentialsCallback.bind(this));
        this.listenTo(dispatcher, DispatcherEvents.LocalStorageFull, this.localStorageFullTasks.bind(this));
        this.listenTo(dispatcher, DispatcherEvents.AddDSL, this.changeStartOfDay.bind(this));
        this.listenTo(dispatcher, DispatcherEvents.WarnOmittedDataOnDSL, this.warnOmittedDataOnDSL.bind(this));
        this.listenTo(dispatcher, DispatcherEvents.All, this.logAllEvents.bind(this));
        this.listenTo(dispatcher, DispatcherEvents.ErrorSendToErrorTracker, this.sendToErrorTrackerImpl.bind(this));
        this.listenTo(dispatcher, DispatcherEvents.ProvideFeedback, this.provideFeedback.bind(this));
        this.listenTo(dispatcher, DispatcherEvents.ErrorHandleJS, this.handleJSImpl.bind(this));
        this.listenTo(dispatcher, DispatcherEvents.ErrorHandleJSNative, this.handleJSNativeImpl.bind(this));
        this.listenTo(dispatcher, DispatcherEvents.ErrorHandleApi, this.handleApiImpl.bind(this));
        this.listenTo(dispatcher, DispatcherEvents.ErrorHandleUnhandledRejection, this.handleUnhandledRejection.bind(this));
        this.listenTo(dispatcher, DispatcherEvents.LoggerTime, time);
        this.listenTo(dispatcher, DispatcherEvents.RemoveUserSessions, this.removeUserSessions.bind(this));
        this.listenTo(dispatcher, DispatcherEvents.LoggerTimeEnd, this.handleTimeEnd.bind(this));
        this.listenTo(dispatcher, DispatcherEvents.LoggerFeedback, this.loggerFeedback.bind(this));
        this.listenTo(dispatcher, DispatcherEvents.HardwareIdCheck, this.onNewIoxUsbDeviceAttached.bind(this));
        this.listenTo(dispatcher, DispatcherEvents.ImageAdded, this.onImageAdded.bind(this));
        this.listenTo(dispatcher, DispatcherEvents.RunVehicleProximity, this.checkVehicleProximity.bind(this));
        this.listenTo(dispatcher, DispatcherEvents.StatusOnline, this.handleNetworkChange.bind(this));
        this.listenTo(dispatcher, DispatcherEvents.IoxAttached, (ioxAttached: boolean) => {
            let previousIoxState = this.store.getState().mobile.ioxAttached;

            if (!ioxAttached) {
                this.store.dispatch(DataActions.setCurrentHardwareId(undefined));
            }

            // If permission is not granted, IOX plugin sends false state repeatedly
            if (!previousIoxState && !ioxAttached) {
                return;
            }
            this.store.dispatch(MobileActions.setIoxAttached(ioxAttached));
        });
        this.listenTo(dispatcher, DispatcherEvents.StatusBackground, (background: IBackground) => {
            this.store.dispatch(MobileActions.setBackground(background));
        });
        this.listenTo(dispatcher, DispatcherEvents.DriveAppInteraction, this.handleDriveAppInteraction.bind(this));
        this.listenTo(dispatcher, DispatcherEvents.HideToastMessage, this.hideToastMessage.bind(this));
        this.listenTo(dispatcher, DispatcherEvents.CheckUnverifiedRuleSetLogs, this.showRuleSetChangePrompt.bind(this));

        this.listenToOnce(dispatcher, DispatcherEvents.ServiceWorkerUpdateSuccess, this.logServiceWorkerPerformance.bind(this));
        this.listenToOnce(dispatcher, DispatcherEvents.ServiceWorkerInstallSuccess, this.logServiceWorkerPerformance.bind(this));
        this.logServiceWorkerPerformance();

        if (!window.serviceWorkerEnabled && window.applicationCache) {
            document.addEventListener("AppCacheDownloaded", (event: Event) => {
                const customEvent = event as AppCacheDownloadedEvent;
                this.addAppCacheDownloadTimeLog(customEvent);
            }, false);

            let uiLoadedEvent = (function () {
                if (typeof Event === "function") {
                    return new Event("UILoaded");
                } else {
                    let event = document.createEvent("Event");
                    event.initEvent("UILoaded", false, false);
                    return event;
                }
            })();
            document.dispatchEvent(uiLoadedEvent);
        }

        if (location.href.indexOf("doLogout") !== -1) {
            void clearLegacyLogoutStorage(this.context.api, true);
        }

        this.onScrollBind = this.onScroll.bind(this);

        this.el.innerHTML = this.template();
        // Topbar and menu rendering are now handled by BackboneWrapper in the React app

        !window.serviceWorkerEnabled && startAppCacheListeners(this.userService);

        void this.databaseCache.refreshMyGeotabProperties();
        void this.databaseCache.updateSourceCodeVersion();

        this.handleNightMode();
        this.handleDebugMode(this.store.getState().data.debugMode);
    }

    get isFullyConnected(): boolean | undefined {
        let isNetwork = this.store.getState().mobile.gpsConnected && this.store.getState().mobile.online === NetworkStates.ONLINE;
        return this.store.getState().mobile.ioxAttached || (isNetwork && (this.store.getState().mobile.deviceCommunicating || isNoVehicle()));
    }

    get api(): Api {
        return this.context.api;
    }

    get manager(): AppManager {
        return this.context.manager;
    }

    get sync(): Sync {
        return this.context.sync;
    }

    get databaseCache(): DatabaseCache {
        return this.context.databaseCache;
    }

    get network(): Network {
        return this.context.network;
    }

    get hosUtils(): HosProcess {
        return this.context.hosProcess;
    }

    get ioxDataHandler(): IoxDataHandler {
        return this.context.ioxDataHandler;
    }

    get screenHandler(): ScreenHandler {
        return this.context.screenHandler;
    }

    get vehicleService(): VehicleService {
        return this.context.vehicleService;
    }

    get translationService(): TranslationService {
        return this.context.translationService;
    }

    get addinService(): AddinService {
        return this.context.addinService;
    }

    get alertsService(): Alerts {
        return this.context.alertsService;
    }

    get newsService(): News {
        return this.context.newsService;
    }

    get securityService(): SecurityService {
        return this.context.securityService;
    }

    get localizerService(): LocalizerService {
        return this.context.localizerService;
    }

    get errorHandler(): ErrorHandler {
        return this.context.errorHandler;
    }

    get dutyStatusLogService(): DutyStatusLogService {
        return this.context.dutyStatusLogService;
    }

    get userHosRuleSetService(): UserHosRuleSetService {
        return this.context.userHosRuleSetService;
    }

    get dvirLogsService(): DVIRLogsService {
        return this.context.dvirLogsService;
    }

    get sessionService() {
        return this.context.sessionService;
    }

    get context(): Context {
        return this._context;
    }

    set context(newContext: Context) {
        // Always ensure the current user is set (important for login flow after page reload)
        if (newContext.user) {
            this.userService.setCurrentActiveUser(newContext.user);
        }

        // Skip subscription restarts if setting to the same context - avoids excessive
        // Subscribe/Unsubscribe API calls (see MYG-97229)
        if (newContext === this._context) {
            // Still refresh DST calculator as it depends on the user which may now be available
            this.refreshDstCalculator();
            return;
        }

        if (this._context) {
            this._context.stopSubscriptions();
        }

        this._context = newContext;
        this.refreshDstCalculator();
        this._context.startSubscriptions();

        if (this.vehicleEvents) {
            this.vehicleEvents.stopWatching();
            this.vehicleEvents.setContext(newContext);
            this.vehicleEvents.startWatching();
        }

        if (this.serviceWorkerEvents) {
            this.serviceWorkerEvents.stopWatching();
            this.serviceWorkerEvents.setContext(newContext);
            this.serviceWorkerEvents.startWatching();
        }
    }


    /* eslint-disable @typescript-eslint/unbound-method */
    events() {
        return {
            "click #scroll-top-button": this.scrollTop,
            "click #debug-shortcut-button": this.navigateToDebugMain
        } as Backbone.EventsHash;
    }
    /* eslint-enable @typescript-eslint/unbound-method */

    needsCredentialsCallback(user: string, errorMessage?: string) {
        let currentUser = this.userService.getCurrentUser();
        let currentPage = Backbone.history.fragment;

        log(Source.USER, "Needs credentials pls! K thx", this.userService.getUsersObject(), currentUser);

        if (currentPage && !this.router.isLoginPage(currentPage)) {
            let navigationPath = "login";
            let loginArguments: any = {};

            // If it's the current user, notify that session has expired
            if (currentUser && currentUser.name && user && currentUser.name.toLowerCase() === user.toLowerCase()) {
                loginArguments.loggingIn = true;
            }

            if (errorMessage) {
                loginArguments.errorMessage = errorMessage;
            }

            this.router.navigateToPage(navigationPath, loginArguments);
        }
    }

    observeStore = <Selected = unknown>(stateSelector: (state: GlobalReducer) => Selected, onChange: (state: Selected) => void) => {
        return observeStore(this.store, stateSelector, onChange);
    };

    setEmbeddedMode(isEmbedded?: boolean) {
        let topbarEl = document.getElementById("topbar");
        if (isEmbedded) {
            topbarEl && topbarEl.classList.add("topbar_embed");
        } else {
            topbarEl && topbarEl.classList.remove("topbar_embed");
        }
    }

    removeContentView() {
        this.contentView?.remove();
        this.contentView = undefined;
    }

    render() {
        // Set HTML lang.
        document.documentElement && document.documentElement.setAttribute("lang", this.getUserLanguage());

        if (!(this.contentView instanceof DebugPageView)) {
            let currentVehicle = new Device(getCurrentVehicle() || {});

            if (currentVehicle.id === "" && !(this.contentView instanceof WorkflowView) && !(this.contentView instanceof LoginView)) {
                logError(
                    Source.ERROR,
                    `AppView.render and getCurrentVehicle.id not found! hash: ${ window.location.hash } getCurrentVehicle: ${ JSON.stringify(getCurrentVehicle()) }`,
                    this.userService.getUsersObject()
                );
                void this.vehicleEvents.assignDriverToVehicle();
                this.spinner && this.spinner.stop();
                this.spinner = null;
                return this;
            }
        }

        this.setEmbeddedMode(this.store.getState().mobile.embed);

        //If a contentView is specified, render it
        if (this.contentView) {
            let contentElement = document.getElementById("content");
            if (!contentElement) {
                contentElement = document.createElement("section");
                contentElement.id = "content";
                (document.getElementById("scroll-content") as Element).appendChild(contentElement);
            }

            if (this.contentView instanceof AddinView) {
                contentElement && contentElement.classList.add("addin-content");
            } else {
                contentElement && contentElement.classList.remove("addin-content");
            }

            let contentId = [this.store.getState().mobile.pageState.activeSection, this.store.getState().mobile.pageState.activeView].filter(i => i).join("-");
            void this.renderSubView(this.contentView, "#content", contentId);
        }

        this.spinner && this.spinner.stop();
        this.spinner = null;

        return this;
    }

    addAppCacheDownloadTimeLog(e: {detail: number}) {
        void this.databaseCache.updateSourceCodeVersion();
        if (!isNaN(e.detail)) {
            logEvent(
                LoggerEvent.APPCACHEDOWNLOADTIME,
                `This is the total time it took to download the appcache: ${ e.detail } seconds.`,
                this.userService.getUsersObject(),
                this.userService.getCurrentUser(),
                e.detail
            );
        }
    }

    handleDriveAppInteraction(e: Event) {
        let target: HTMLElement | HTMLSelectElement = e.currentTarget as any;

        if ((e instanceof CustomEvent && e.detail) || (target && target.tagName)) {
            let eType = e.type ? `${ e.type } ` : "";
            let text = "";
            let targetId = "";

            if (target && target.tagName) {
                targetId = `#${ target.id } `;
                // Never read the value of password inputs — log the element + event type only.
                // Defense-in-depth alongside cleanSessionAndPassword (MYG-142393): even if the
                // sanitizer is bypassed, credentials never enter the payload.
                //
                // type check: catches password inputs in their normal state.
                // name check: catches inputs whose type has been toggled to "text" by showPassword()
                //             while still representing a password field (name does not change).
                //
                // This guard is preemptive — handleDriveAppInteraction is only reached via
                // Backbone event delegation and no current events() hash selector targets a
                // password input directly. It protects against future regressions.
                const inp = target as HTMLInputElement;
                const isPasswordField = target.tagName === "INPUT" && (
                    inp.type.toLowerCase() === "password"
                    || inp.name.toLowerCase() === "password"
                );
                if (!isPasswordField) {
                    text = (target as HTMLSelectElement).value || target.innerText;
                }
            } else if (e instanceof CustomEvent) {
                targetId = `${ e.detail.id } `;
                text = e.detail.innerText || "";
            }

            let cleanedText = text ? `(${ text.replace(/(\r\n|\n|\r)/gm, " ").trim() }) ` : "";
            let hashString = this.store.getState().mobile.currentPage;
            log(Source.USER_EVENT, `${ eType }${ targetId }${ cleanedText }${ hashString }`, this.userService.getUsersObject());
        }
    }

    /**
     * Renders a subview (topbar, menu, content) asynchronously.
     * Also responsible for triggering translation of the view & some HTML scrolling logic.
     *
     * @param view - The DriveView instance to render.
     * @param selector - The selector for the root element where the view will be rendered.
     * @param contentId - Optional. The content ID for setting the attribute of the root element.
     * @returns A CancelablePromise that resolves when the subview is rendered.
     * @throws Error if trying to render React component and root HTML element is not found or React element is invalid.
     */
    renderSubView(view: DriveView<Backbone.Model>, selector: string, contentId?: string): CancelablePromise<void> {
        let tabViews = ["HosView", "LogsView"];
        this.translating[selector] = view;

        return view.translate()?.then(() => {
            if (this.translating[selector] === view) {
                let viewElement = document.querySelector(selector);
                if (viewElement) {
                    // Only some views we can set aria-labelledby attribute for, (mainly, the pages that are controlled with tab controls like the Hos logs & main views).
                    let attributeName = tabViews.indexOf((view.constructor as any).name) !== -1 ? "aria-labelledBy" : "aria-label";

                    if (attributeName === "aria-labelledBy") {
                        contentId && viewElement.setAttribute(attributeName, `hosSubmenu-${ contentId === "hos-status" ? "StatusLabel" : "LogsLabel" }`);
                    } else {
                        contentId && viewElement.setAttribute(attributeName, contentId);
                    }
                    view.setElement(viewElement).render();
                }
                this.shouldShowScrollBtn = view.showScrollBtn;
                this.setScrollListener(this.shouldShowScrollBtn);
                delete this.translating[selector];
            }
        }).catch(handleApi);
    }

    /*
    App View keeps track of previous charging state so once changed
    it will keep the screen on, and restart the gps. This is more functional
    than the status.get('charging')
     */
    changeCharging(isCharging?: boolean) {
        log(Source.SERVICES, `Charging? ${ isCharging }`, this.userService.getUsersObject());
        keepScreenOn(isCharging ? isCharging : false);
    }

    // eslint-disable-next-line complexity
    lockScreen(driverName: string | false) {
        let url = Backbone.history.fragment;
        // Don't change or do anything to the lockscreen if we're in the process of logging in a new driver
        if (url && url.indexOf("login,(addCoDriver:true)") !== -1) {
            this.cachedContentView = null;
            return false;
        }

        if (driverName && this.userService.isDriverUsingUI(driverName)) {
            const alreadyLocked = this.store.getState().mobile.pageState.isLocked;
            if (alreadyLocked) {
                log(Source.USER_EVENT, "Screen is already locked, skipping lockScreen execution", this.userService.getUsersObject());
                return;
            }

            const isBackboneLockscreen = !this.cachedContentView && this.contentView;

            if (isBackboneLockscreen) {
                dispatcher.trigger(DispatcherEvents.LockscreenWillToggle);
                this.contentViewCachePath = Backbone.history.fragment;

                if (this.contentView) {
                    this.cachedContentView = this.contentView.constructor as TDriveViewConstructor<Backbone.Model>;
                    this.contentViewCacheOptions = this.contentView.options;
                    this.removeContentView();
                }

                this.contentView = new LockScreenView({ app: this });
                this.render();
                log(Source.USER_EVENT, "Lockscreen ON", this.userService.getUsersObject());
            } else if (!this.cachedContentView && !this.contentView) {
                this.router.navigate("main", { trigger: true, replace: true });
                log(Source.USER_EVENT, "Lockscreen fallback: redirecting to main", this.userService.getUsersObject());
            }
        } else {
            let apiForRequest = this.api;
            let apiUser = apiForRequest && apiForRequest.getUser();
            let activeDriver = this.userService.getDriversSeatUser();

            // KNOWN ISSUE (MYG-143588): this branch is not re-entrancy-safe. cachedContentView
            // is checked below and used at the `new this.cachedContentView(...)` call, but the
            // removeContentView() in between runs LockView.remove(), which dispatches
            // setPageState synchronously. Redux notifies subscribers inline, so any subscriber
            // that calls lockScreen() again re-enters this method, clears cachedContentView, and
            // the outer frame then throws "this.cachedContentView is not a constructor" —
            // orphaning a #submenu (see SubnavigationBaseView). Deliberately left as-is: the
            // known trigger (a resurrected Context restarting its services) is fixed at source,
            // and Context.startSubscriptions() now reports any remaining duplicate-subscription
            // path. If this is hardened later, snapshot the constructor and clear the field
            // BEFORE removeContentView(), then construct inside a try/catch with a
            // DashboardView fallback so a throw cannot leave contentView undefined.
            // Don't switch back to a cached page from the "active" driver if we've switched users
            if (apiUser &&
                apiUser.name &&
                activeDriver &&
                sanitizeUserName(apiUser.name) === sanitizeUserName(activeDriver.name) &&
                this.cachedContentView &&
                this.contentViewCachePath && // if leaving inspection and dialog open, contentViewCachePath != contentView
                (this.contentViewCachePath === Backbone.history.fragment || Backbone.history.fragment.indexOf("inspection") > -1)
            ) {
                this.removeContentView();

                // Only change the view back if the lock screen was opened and the user didn't change the page in the meantime
                this.contentView = new this.cachedContentView(this.contentViewCacheOptions || { app: this }); // Needed to re-initialize events that were killed off
                this.cachedContentView = null;
                this.render();
                log(Source.USER_EVENT, "Lockscreen OFF", this.userService.getUsersObject());
            }

            // Fallback: if contentView is still a LockScreenView, force-clear the lockscreen
            if (this.contentView instanceof LockScreenView) {
                this.removeContentView();
                this.contentView = new DashboardView({ app: this });
                this.render();
                log(Source.USER_EVENT, "Lockscreen OFF (fallback)", this.userService.getUsersObject());
            }

            this.cachedContentView = null;
        }

        return undefined;
    }

    pageLoaded(pageName: string) {
        this.store.dispatch(MobileActions.setPageLoaded(pageName));
        if (pageName.indexOf("login") === -1 && this.screenHandler) {
            this.screenHandler.drivingStatusChanged();
        }

        // Re-evaluate a prompt that was suppressed while a DVIR was open (MYG-151386), now that the
        // driver has navigated away from it. Clear first so a re-suppression (e.g. re-entering the
        // DVIR, or another ignore condition) cannot re-stash the same reference and loop.
        if (this.pendingDvirPrompt && !isDriverViewingDvirMode()) {
            const { slimLog, options } = this.pendingDvirPrompt;
            this.pendingDvirPrompt = null;
            this.promptDriverFromLog(slimLog, options);
        }

        let isPageBlockedFromExecutingAddinStartup = this.addinStartupBlockedPages.some(blockPageName => pageName.toLowerCase().includes(blockPageName));
        if (!isPageBlockedFromExecutingAddinStartup) {
            let dashboardAddins = collectDashboardAddins(this.databaseCache, this.userService);
            if (dashboardAddins.length) {
                executeStartupAddins(dashboardAddins, this.databaseCache, this.userService, this.store, this.router, this.addinService, this.api);
            }
        }
    }

    showContinueExemptionDialogOrExitExemptionForDriver(latestLog: DutyStatusLog, newVehicle: IIdEntity, serverDate: Date): CancelablePromise {
        const currentDriver = this.userService.getDriversSeatUser();
        if (!currentDriver) {
            return CancelablePromise.resolve();
        }
        const castedDriver = this.userService.getUserById(latestLog.driver.id) as IUser;
        if (!castedDriver) {
            return CancelablePromise.resolve();
        }
        const isCodriver = currentDriver.id !== latestLog.driver.id;
        const showPrompt = !isCodriver && newVehicle.id !== "NoDeviceId";
        this.addLogsToEndDrivingExemption(castedDriver, latestLog, new Date(serverDate.getTime() - 5), "OtherUser");

        if (!showPrompt) {
            return CancelablePromise.resolve();
        }

        let exemptionDialog = new Dialog(
            "exemption-confirmation",
            "Continue exemption with new vehicle?",
            this.context.translationService.getTranslator(),
            "Exemption Confirmation",
            [
                [
                    {
                        id: "continue-exemption-denied",
                        text: "End exemption",
                        className: "button_secondary",
                        callback: () => {
                            exemptionDialog.remove();
                        }
                    },
                    {
                        id: "continue-exemption-accepted",
                        text: "Yes",
                        callback: () => {
                            exemptionDialog.remove();
                            this.addManualLogAfterPromptLog(castedDriver, new Date(serverDate.getTime() + 5), newVehicle, latestLog.status, "", "Manual");
                        }
                    }
                ]
            ],
            {
                closable: false,
                isNightMode: this.store.getState().data.nightMode
            }
        );

        return exemptionDialog.open().catch((err: IApiError) => {
            this.errorHandler.handleApiImpl(err, true);
        });
    }

    async localStorageFullTasks(): Promise<CancelablePromise> {
        this.errorHandler.sendToErrorTrackerImpl(new Error("Local storage has reached its size quota"), "javascript");

        // Remove keys containing logging or keys belonging to inactive users, it's not important to retain those as their data isn't being utlized for anything currently
        let stringsToRemove = [`${ DRIVE_STORAGE_PREFIX }@logs`, `${ DRIVE_STORAGE_PREFIX }@logs_uploadq`, `${ DRIVE_STORAGE_PREFIX }ADDIN`];
        for (const key in localStorage) {
            if (!localStorage.hasOwnProperty(key)) {
                continue;
            }
            if (key.indexOf(DRIVE_STORAGE_PREFIX) === -1 && key.indexOf("driveAPICredentials") === -1) {
                stringsToRemove.push(key);
            }
        }

        // Remove keys THEN setUsers, calling 'set' before clearing space can cause a loop of storage full errors
        if (IndexedDbMigrationFromRedux.isIndexedDbInUse) {
            await IndexedDbStorage.removeAllKeysContainingStrings(stringsToRemove);
        } else {
            storage.removeAllKeysContainingStrings(stringsToRemove);
        }

        this.removeTrailerCache();

        dispatcher.trigger(DispatcherEvents.RemoveUserSessions);

        let serverDate = getServerDate();
        serverDate.setDate(serverDate.getDate() - 14);
        this.store.dispatch(DataActions.clearAddressCache(serverDate));

        // Remove all driver change logs that have an ID (i.e. have been synced to the server)
        let driverChangeLogs = new DriverChangeLogs(this.api, this.store);
        driverChangeLogs.setLogs(driverChangeLogs.getNewLogs());

        // Clear addins from LocalStorage

        let dialog = new Dialog(
            "localstorage-full",
            "The application has reached its storage capacity limit. Your data will be synced and the application cache cleared.",
            this.context.translationService.getTranslator(),
            "Confirmation Required",
            [[{
                id: "localstorage-full",
                text: "OK",
                callback: () => {
                    dialog.remove();
                }
            }]],
            {
                closable: true,
                isNightMode: this.store.getState().data.nightMode
            }
        );

        // Use the credentials in case the user object is missing
        let currentUser = this.api.getCurrentCredentials();
        let currentUserName = currentUser?.credentials?.userName;

        const dialogResult = await dialog.open()
            .then(applyDriverActionNecessary(this.store, webViewLayer.driverActionTypes.StorageCapacityReached))
            .then(() => this.addDataRecordingComplianceLog("Malfunction"))
            .then(() => currentUserName ? new UserUpdater(this.api, this.userService, this.securityService, this.store).updateUser(currentUserName) : Promise.resolve())
            .then(() => {
                // Do a full sync, this should remove older logs from local storage upon completion
                return this.context.sync.synchronize(true);
            })
            // Until we reliably cover unhandledrejection events, need to catch
            .catch((err) => {
                this.errorHandler.handleApiImpl(err, true);
            });

        return dialogResult;
    }

    removeTrailerCache() {
        if (IndexedDbMigrationFromRedux.isIndexedDbInUse) {
            // Used FF so that we can clear
            if (this.store.getState().cache) {
                Object.keys(this.store.getState().cache).forEach(userId => {
                    this.store.dispatch(CacheActions.clearCache({ cacheType: "trailer", userId: userId }));
                });
            }
        } else {
            const cacheKey = `${ DRIVE_STORAGE_PREFIX }${ StorageKeys.CACHE_KEY }`;
            this.store.dispatch(CacheActions.clearCache({ cacheType: "trailer", userId: "" }));
            if (localStorage.hasOwnProperty(cacheKey)) {
                const currentStorage = storage.get<IIdDictionary<ICache>>(cacheKey, true, undefined, true) ?? {};
                Object.keys(currentStorage).forEach(key => {
                    this.store.dispatch(CacheActions.clearCache({ cacheType: "trailer", userId: key }));
                });
            }
        }
    }

    changeStartOfDay(currentLog: DutyStatusLog): CancelablePromise {
        return this.context.changeStartOfDayService.offerChangeStartOfDay(currentLog).catch(handleApi);
    }

    warnOmittedDataOnDSL(currentLog: DutyStatusLog) {
        this.context.createLogWithOmittedDataWarningService.showLogCreatedWithOmittedDataWarning(currentLog);
    }

    addDataRecordingComplianceLog = (malfunctionType: THosMalfunction): CancelablePromise => {
        let currentVehicle = getCurrentVehicle();
        if (currentVehicle && currentVehicle.id && currentVehicle.id !== "NoDeviceId") {
            let newLog = new DutyStatusLog({
                id: idSerializer.get(),
                dateTime: getServerDate().toISOString(),
                status: "DataRecordingCompliance",
                malfunction: malfunctionType,
                device: { id: currentVehicle.id },
                driver: { id: "NoUserId" },
                origin: "Automatic"
            });
            // Set it to sync later because when this method is called, a full sync occurs almost immediately afterwards so it'd be redundant to do it now
            return this.sync.dutyStatusLogs.addItem(newLog, true);
        }
        return CancelablePromise.resolve();
    };

    promptDutyStatusAddressesImpl(): CancelablePromise {
        let dialogId = "prompt-dutystatus-address";

        // If this dialog is already open, don't open again.
        // Also, don't open if this is during driving. The lock screen should prompt on there.
        // Don't open if the driver is currently using inspection mode.
        if (document.getElementById(dialogId) || (this.contentView && this.contentView instanceof LockScreenView) || isDriverViewingInspectionMode()) {
            return CancelablePromise.resolve();
        }

        let dialogMessage = "<p>There are duty status change logs needing manual location entry due to an error or malfunction.</p>";
        let buttons: IDialogButton[];
        let closable = true;

        dialogMessage += "<p>Review logs and correct any locations marked as missing.</p>";
        buttons = [
            {
                id: "prompt-dutystatus-address-button-view-logs",
                text: "View logs",
                callback: dialog => {
                    dialog.remove();
                    this.router.navigate("hos/tabs/logs/logs", true);
                }
            }
        ];

        return new Dialog(dialogId, dialogMessage, this.context.translationService.getTranslator(), `Missing Duty Status Locations`, buttons, {
            closable,
            isNightMode: this.store.getState().data.nightMode
        })
            .open()
            .then(applyDriverActionNecessary(this.store, webViewLayer.driverActionTypes.PositionMalfunction))
            .catch((err) => {
                this.errorHandler.handleApiImpl(err, true);
            });
    }

    promptDutyStatusAddresses(): Promise<Dialog | undefined> {
        this.stopListening(dispatcher, DispatcherEvents.CompleteWorkflow);
        if (this.contentView && (this.contentView instanceof WorkflowView || this.contentView instanceof LoginView)) {
            this.listenTo(dispatcher, DispatcherEvents.CompleteWorkflow, () => {
                this.stopListening(dispatcher, DispatcherEvents.CompleteWorkflow);
                return this.promptDutyStatusAddressesImpl();
            });
            return CancelablePromise.resolve();
        }

        return this.promptDutyStatusAddressesImpl();
    }

    getNavigationLocker() {
        return this.contentView && this.contentView.onBeforeUnload;
    }

    showRuleSetChangePrompt() {
        // Use shared utility to get unverified logs
        const logsToVerify = getUnverifiedZoneCycleLogs(this);

        // Use shared utility to check if dialog should be shown (isZenithContext=false for Backbone)
        if (!shouldShowZoneCycleDialog(this, logsToVerify, false)) {
            return;
        }

        this.stopListening(dispatcher, DispatcherEvents.CompleteWorkflow);
        if (isDriverOnAnyWorkflow()) {
            this.listenTo(dispatcher, DispatcherEvents.CompleteWorkflow, () => {
                this.stopListening(dispatcher, DispatcherEvents.CompleteWorkflow);
                this.showRuleSetChangePrompt();
            });
            return;
        }

        let zoneCycleChangeDialog = new ZoneCycleChangeDialog({
            app: this,
            logsToVerify
        });
        zoneCycleChangeDialog.open().catch(handleApi);
    }

    promptDriverFromLog(slimLog?: DutyStatusLog, options?: IVehicleEndedStatusOptions): CancelablePromise {
        if (!slimLog) {
            const { driving } = this.store.getState().data;
            // The vehicle resuming driving (observeStore(state.data.driving) below) also routes here
            // with no slimLog. If a DVIR-suppressed stop prompt was pending, the stop condition it
            // represented is gone now that the vehicle is moving again - drop it so pageLoaded()
            // can't later replay a stale "stop detected" prompt once the driver leaves the DVIR.
            this.pendingDvirPrompt = null;
            return this.closeDriverLogPrompt(`cleared while open (no active log, driving=${ driving })`);
        }

        let currentDriver = this.userService.getDriversSeatUser();
        let currentVehicle = getCurrentVehicle();

        if (!currentDriver || !currentVehicle || this.checkIfPromptShouldBeIgnored()) {
            const reason = !currentDriver ? "no currentDriver"
                : !currentVehicle ? "no currentVehicle"
                : "checkIfPromptShouldBeIgnored";
            this.logDriverLogPromptEvent(`Prompt suppressed: ${ reason }`, slimLog);
            // While a DVIR is open, stash the suppressed prompt so it can be re-evaluated once the
            // driver leaves the DVIR (pageLoaded()) instead of being dropped permanently - the
            // underlying duty-status condition (e.g. vehicle stopped) hasn't gone away just because
            // the inspection closed.
            if (reason === "checkIfPromptShouldBeIgnored" && isDriverViewingDvirMode()) {
                this.pendingDvirPrompt = { slimLog, options };
            }
            return CancelablePromise.resolve();
        }
        this.pendingDvirPrompt = null;

        // Fetch the last log for the user
        let slimLogDriverId = slimLog.driver && slimLog.driver.id;
        let collection = this.dutyStatusLogService.getSortedHosLogs(this.dutyStatusLogService.getHOSLogs(false, slimLogDriverId).filter(hosLog => hosLog.isStandardStatusDriverLog()));
        let dutyStatusLog = this.dutyStatusLogService.getLogById(collection, slimLog.id) as DutyStatusLog;
        let isIoxLog = dutyStatusLog && dutyStatusLog._isTempIoxLog;

        // If it's an IoxLog, fallback to old logic of showing the prompt based on the standard log.
        if (!isIoxLog) {
            this.logDriverLogPromptEvent("VehicleEndedStatus received for non-IoX log", slimLog);
            return this.showPromptDriverFromLog(slimLog, currentDriver, currentVehicle, options);
        }

        let latestLog = this.dutyStatusLogService.getLatestLog(collection);
        let logBeforePromptLog = this.dutyStatusLogService.getLatestLog(collection, dutyStatusLog);

        // Number of reasons why we won't show dialog
        // Can't find the log or the latest log
        // The latest log is not the log (driver already changed status)
        // currentDriver or currentVehicle does not match state of app, status should have ended
        let currentDriverId = currentDriver.id;
        if (!dutyStatusLog || !latestLog || dutyStatusLog.id !== latestLog.id || currentDriverId !== dutyStatusLog.getDriverId() || currentVehicle.id !== dutyStatusLog.getDeviceId()) {
            return CancelablePromise.resolve();
        }

        let logStatus = dutyStatusLog.status;
        const isEndedPcPrompt = logStatus === "OFF" && logBeforePromptLog?.status === "PC";
        const isEndedYmPrompt = logStatus === "ON" && logBeforePromptLog?.status === "YM";
        const isStoppedDrivingPrompt = logStatus === "ON";

        const promptType = this.resolvePromptDialogType(isEndedPcPrompt, isEndedYmPrompt, isStoppedDrivingPrompt);
        if (!promptType) {
            return CancelablePromise.resolve();
        }
        const { newStatus, dialogText, selfClosingTimeLimit } = promptType;

        // If the log before is not the expected status, this is not an end-exemption situation.
        if (this.shouldSuppressPromptForLogBefore(logBeforePromptLog, newStatus)) {
            return CancelablePromise.resolve();
        }

        // Engine was cycled but the driver is continuing PC: convert the engine-cycle log to a
        // manual PC log and suppress the prompt — no confirmation is needed.
        if (isEndedPcPrompt && logBeforePromptLog && isDriverContinuingPC(logBeforePromptLog, slimLogDriverId, this.dutyStatusLogService)) {
            this.editEngineCycleLogToManualLog(dutyStatusLog, newStatus);
            return CancelablePromise.resolve();
        }

        // Exemption permission was revoked — suppress prompt, the engine-cycle OFF log already ended PC.
        if (this.isExemptionPermissionRevoked(isEndedPcPrompt, isEndedYmPrompt, currentDriver)) {
            return CancelablePromise.resolve();
        }

        let userPromptDialog = new Dialog(
            "user-log-prompt-dialog",
            `<span class="noTranslate">${ htmlEscape(currentDriver.name) }:</span><br>The system has detected that you have ${ dialogText } Is this correct?`,
            this.context.translationService.getTranslator(),
            `Confirmation Required`,
            [[
                {
                    id: "stay-in-status",
                    text: "No",
                    className: "button_secondary",
                    callback: () => this.handleIoxPromptNo(isEndedPcPrompt, isEndedYmPrompt, dutyStatusLog, newStatus, currentDriver as IUser, userPromptDialog)
                }, {
                    id: "stopped-status",
                    text: "Yes",
                    callback: () => {
                        userPromptDialog.remove();
                    }
                }
            ]],
            {
                closable: false,
                isSelfClosing: selfClosingTimeLimit,
                isNightMode: this.store.getState().data.nightMode
            }
        );

        return userPromptDialog.open()
            .then(applyDriverActionNecessary(this.store, webViewLayer.driverActionTypes.ConfirmHOSStatus))
            .catch((err) => {
                this.errorHandler.handleApiImpl(err, true);
            });
    }

    getFilteredLogCollection(slimLog: DutyStatusLog, slimLogDriverId: string) {
        const includeSystemLogs = slimLog.isMotionStoppedLog() || slimLog.isEnginePowerupPcLog();
        const logFilter = (hosLog: DutyStatusLog) => hosLog.isStandardStatusDriverLog();
        return this.dutyStatusLogService.getSortedHosLogs(this.dutyStatusLogService.getHOSLogs(false, slimLogDriverId, false, includeSystemLogs).filter(logFilter));
    }

    getLatestLog(promptLog: DutyStatusLog, collection: DutyStatusLog[]) {
        const includeSystemLogs = promptLog.isMotionStoppedLog() || promptLog.isEnginePowerupPcLog();
        const logFilter = (hosLog: DutyStatusLog) => hosLog.isNormalLogImpl() || hosLog.isMotionStoppedLog() || hosLog.isEnginePowerupPcLog();
        return includeSystemLogs ? this.dutyStatusLogService.getLatestLog(collection, undefined, logFilter) :  this.dutyStatusLogService.getLatestLog(collection);
    }

    evaluatePromptDetails(
        promptLog: DutyStatusLog,
        logBeforePromptLog: DutyStatusLog | null,
        collection: DutyStatusLog[],
        currentDriver: DeepReadonlyObject<IUser>,
        options?: IVehicleEndedStatusOptions
    ): IPromptOptions {
        let isEndedPcPrompt = promptLog.isEnginePowerupPcLog() && logBeforePromptLog?.isPCLog() && this.isIgnCycleAfterPc(promptLog, collection) || false;
        let isEndedYmPrompt = promptLog.isOnLog() && logBeforePromptLog?.isYmLog();
        let isStoppedDrivingPrompt = promptLog.isMotionStoppedLog() && logBeforePromptLog?.isAutomaticDriving();
        const isEndedPcViaSdc = !!options?.sdcEndedPc && !!logBeforePromptLog?.isPCLog();
        const confirmationRequired = "Confirmation Required";
        const displayMessagePrefix = `<span class=\"noTranslate\">${ htmlEscape(currentDriver.name) }:</span><br>The system has detected that you have ended`;
        const displayMessageSuffix = "Is this correct?";

        if (isEndedYmPrompt && isInCanada(this.hosUtils.getLatestHosRuleSetIdShortened())) {
            return {
                shouldShowPrompt: true,
                newStatus: "YM" as THosStatus,
                title: confirmationRequired,
                message: `${ displayMessagePrefix } yard move. ${ displayMessageSuffix }`,
                isEndedYmPrompt };
        } else if (isEndedPcPrompt) {
            return {
                shouldShowPrompt: true,
                newStatus: "PC" as THosStatus,
                title: confirmationRequired,
                message: `${ displayMessagePrefix } personal conveyance. ${ displayMessageSuffix }`,
                isEndedPcPrompt };
        } else if (isEndedPcViaSdc) {
            return {
                shouldShowPrompt: true,
                newStatus: "PC" as THosStatus,
                title: confirmationRequired,
                message: `${ displayMessagePrefix } personal conveyance. ${ displayMessageSuffix }`,
                isEndedPcViaSdc };
        } else if (isStoppedDrivingPrompt) {
            return {
                shouldShowPrompt: true,
                newStatus: "D" as THosStatus,
                title: "A stop was detected",
                message: "Switch from Drive to On-Duty?",
                isStoppedDrivingPrompt,
                selfClosingTimeLimit: constants.DIALOG.SELF_CLOSING_TIMEOUT_SECS };
        }

        return { shouldShowPrompt: false };
    }

    promptPreconditionsFail(currentDriver: DeepReadonlyObject<IUser>, currentVehicle: IIdEntity, promptLog: DutyStatusLog, latestLog: DutyStatusLog | null) {
        return !promptLog || !latestLog || promptLog.id !== latestLog.id || currentDriver.id !== promptLog.getDriverId() || currentVehicle.id !== promptLog.getDeviceId();
    }

    showUserPromptDialog(
        currentDriver: DeepReadonlyObject<IUser>,
        promptLog: DutyStatusLog,
        promptOptions: IPromptOptions,
        logBeforePromptLog: DutyStatusLog | null = null,
        onPromptPresented?: (dialog: Dialog) => void,
        onPromptHandled?: () => void
    ) {
        const castedCurrentDriver = currentDriver as IUser;
        let userPromptDialog: Dialog = new Dialog(
            "user-log-prompt-dialog",
            promptOptions.message as string,
            this.context.translationService.getTranslator(),
            promptOptions.title,
            [
                [{
                    id: "stay-in-status",
                    text: "No",
                    className: "button_secondary",
                    callback: () => this.handleAction(castedCurrentDriver, promptLog, promptOptions, false, userPromptDialog, logBeforePromptLog, onPromptHandled)
                }, {
                    id: "stopped-status",
                    text: "Yes",
                    callback: () => this.handleAction(castedCurrentDriver, promptLog, promptOptions, true, userPromptDialog, logBeforePromptLog, onPromptHandled)
                }]
            ],
            {
                closable: false,
                isSelfClosing: promptOptions.selfClosingTimeLimit,
                isNightMode: this.store.getState().data.nightMode
            }
        );
        onPromptPresented && userPromptDialog.onOpen(() => onPromptPresented(userPromptDialog));
        return userPromptDialog.open();
    }

    handleAction = (
        currentDriver: IUser,
        promptLog: DutyStatusLog,
        promptOptions: IPromptOptions,
        ended: boolean,
        userPromptDialog: Dialog,
        logBeforePromptLog: DutyStatusLog | null = null,
        onPromptHandled?: () => void
    ) => {
        if (promptOptions.isEndedPcPrompt) {
            this.handlePcPrompt(currentDriver, promptLog, promptOptions.newStatus, ended, logBeforePromptLog);
        } else if (promptOptions.isEndedPcViaSdc) {
            this.handleSdcEndedPcPrompt(currentDriver, promptLog, promptOptions.newStatus, ended);
        } else if (promptOptions.isEndedYmPrompt) {
            this.handleYmPrompt(promptLog, promptOptions.newStatus, ended);
        } else if (promptOptions.isStoppedDrivingPrompt) {
            this.handleStoppedPrompt(currentDriver, promptLog, ended);
        }
        onPromptHandled?.();
        userPromptDialog.remove();
    };

    handlePcPrompt = (
        currentDriver: IUser,
        promptLog: DutyStatusLog,
        newStatus: THosStatus | null | undefined,
        ended: boolean,
        logBeforePromptLog: DutyStatusLog | null = null,
        origin?: THosOrigin
    ) => {
        const device = promptLog.device;

        if (ended) {
            this.addLogsToEndDrivingExemption(currentDriver, logBeforePromptLog, getServerDate(), origin || "Manual");
        } else {
            let logDate: Date = new Date(promptLog.getDate().toISOString());
            logDate.setSeconds(logDate.getSeconds() + 1);
            newStatus && this.addManualLogAfterPromptLog(currentDriver, logDate, device, newStatus);
        }
    };

    handleSdcEndedPcPrompt = (currentDriver: IUser, promptLog: DutyStatusLog, newStatus: THosStatus | null | undefined, ended: boolean) => {
        if (ended) {
            return;
        }

        const logDate = new Date(promptLog.getDate().toISOString());
        logDate.setSeconds(logDate.getSeconds() + 1);
        newStatus && this.addManualLogAfterPromptLog(currentDriver, logDate, promptLog.device, newStatus);
    };

    handleYmPrompt = (promptLog: DutyStatusLog, newStatus: THosStatus | null | undefined, ended: boolean) => {
        if (!ended && newStatus) {
            this.editEngineCycleLogToManualLog(promptLog, newStatus);
        }
    };

    handleStoppedPrompt = (currentDriver: IUser, promptLog: DutyStatusLog, ended: boolean) => {
        const device = promptLog.device;
        if (ended) {
            this.addManualLogAfterPromptLog(currentDriver, new Date(), device, "ON");
        } else {
            this.addManualLogAfterPromptLog(currentDriver, new Date(), device, "D");
        }
    };

    isIgnCycleAfterPc(slimLog: DutyStatusLog, collection: DutyStatusLog[]) {
        let dutyStatusLog = this.dutyStatusLogService.getLogById(collection, slimLog.id) as DutyStatusLog;
        let epuPcIndex = collection.indexOf(dutyStatusLog);
        let logBeforeEpuPc: DutyStatusLog | undefined;

        if (epuPcIndex >= 1) {
            logBeforeEpuPc = collection[epuPcIndex - 1];
        }
        return !!(logBeforeEpuPc && logBeforeEpuPc.status === "EngineShutdownPC");
    }

    checkIfPromptShouldBeIgnored(): boolean {
        // Don't want to see this prompt while logging in. Ignore while on workflow/login. Ignore while in
        // inspection mode (Roadside Check) or while an open DVIR asset inspection is in progress (MYG-151386).
        if (!this.contentView || this.contentView instanceof WorkflowView || this.contentView instanceof LoginView || isDriverViewingInspectionMode() || isDriverViewingDvirMode()) {
            return true;
        }

        // Don't open if dialog is already open. Also ignore if user's changed vehicles/drivers, do not show.
        if (document.getElementById("user-log-prompt-dialog") || document.getElementById("vehicle-removed-dialog")) {
            return true;
        }

        return false;
    }

    shouldSuppressPromptForLogBefore(logBeforePromptLog: DutyStatusLog | null, newStatus: THosStatus): boolean {
        return !logBeforePromptLog || (logBeforePromptLog.status !== newStatus && !(newStatus === "PC" && logBeforePromptLog.isPCLog()));
    }

    editEngineCycleLogToManualLog(promptLog: DutyStatusLog, newStatus: THosStatus) {
        promptLog.status = newStatus;
        promptLog.origin = "Manual";
        promptLog.editDateTime = getServerDate().toISOString();
        if (promptLog._isTempIoxLog) {
            let currentActions = this.store.getState().data.ioxLogActions;
            this.store.dispatch(DataActions.setIoxLogAction({
                ...currentActions,
                [getServerDate().toISOString()]: newStatus === "PC" ? IoxActionTypes.SET_AUTOOFF_TO_PC : IoxActionTypes.SET_AUTOON_TO_D
            }));
        }
        dispatcher.trigger(DispatcherEvents.UserSetDSLForDriver, promptLog);
    }

    addManualLogAfterPromptLog(currentDriver: IUser, logDate: Date, device: IIdEntity, newStatus: THosStatus, annotation = "", origin: THosOrigin = "Manual") {
        let newLog = this.dutyStatusLogService.createManualDutyStatusLog(
            logDate.toISOString(),
            device,
            currentDriver,
            this.userService.getCoDrivers(currentDriver.name),
            newStatus,
            origin,
            annotation
        );
        dispatcher.trigger(DispatcherEvents.UserAddDSLForDriver, newLog, false);
    }

    addLogsToEndDrivingExemption(driver: IUser, exemptionLog: DutyStatusLog | null, exemptionEndDate: Date, origin: THosOrigin) {
        const newLogs = this.dutyStatusLogService.getLogsToEndDrivingExemption(driver, exemptionLog, exemptionEndDate, origin);
        newLogs.forEach((newLog, index) => {
            dispatcher.trigger(DispatcherEvents.UserAddDSLForDriver, newLog, index !== newLogs.length - 1);
        });
    }

    getUserLanguage(): string {
        let currentUser = this.userService.getCurrentUser();
        return currentUser && currentUser.language ? currentUser.language : "en";
    }

    toggleButton() {
        let buttonElement = document.getElementById("scroll-top-button");
        if (buttonElement) {
            if (this.isScrollBtnVisible) {
                buttonElement.classList.add("show");
            } else {
                buttonElement.classList.remove("show");
            }
        }
    }

    setFullWidth(isFullWidth: boolean) {
        let driverContainer = document.getElementById("content");
        if (driverContainer) {
            if (isFullWidth) {
                driverContainer.classList.add("full-width-content");
            } else {
                driverContainer.classList.remove("full-width-content");
            }
        }
    }

    sendToErrorTrackerImpl(err: IExtendedError, tag: string) {
        this.errorHandler.sendToErrorTrackerImpl(err, tag);
    }

    handleJSImpl(message: string | object, url: string | null, lineNumber: number | null, column: number | null, error: Error, tag = "javascript") {
        this.errorHandler.handleJSImpl(message, url, lineNumber, column, error, tag);
    }

    handleApiImpl(errorObject: any, silenceError?: boolean) {
        this.errorHandler.handleApiImpl(errorObject, silenceError);
    }

    handleJSNativeImpl(e: Error, silenceError?: boolean, tag?: string): void {
        this.errorHandler.handleJSNativeImpl(e, silenceError, tag);
    }

    handleUnhandledRejection(event: Event): void {
        this.errorHandler.handleUnhandledRejection(event);
    }

    provideFeedback() {
        this.errorHandler.provideFeedback();
    }

    handleNetworkChange(isOnline: boolean, force?: boolean) {
        let previousStatus = this.store.getState().mobile.online;
        let isCurrentlyOnline = previousStatus === NetworkStates.ONLINE;

        // Return early if forced to skip or if the state didn't change
        if (force || isOnline === isCurrentlyOnline) {
            return;
        }

        // Dispatch this first, using generic offline state as we don't yet know what kind of offline state it is
        this.store.dispatch(MobileActions.setOnline(isOnline ? NetworkStates.ONLINE : NetworkStates.OFFLINE));

        // Add a debounce in case multiple network calls come in at once
        this.checkingNetworkStatusTask && clearTimeout(this.checkingNetworkStatusTask);
        this.checkingNetworkStatusTask = null;
        this.checkingNetworkStatusTask = setTimeout(() => {
            this.checkingNetworkStatusTask = null;

            // Do a quick getversion to verify if the server is up or not
            Promise.all([this.databaseCache.refreshMyGeotabProperties(), this.databaseCache.getLoginServerVersion()])
                .then(([serverObject, loginServerVersion]) => {
                    let serverVersion = serverObject?.application?.full || "";
                    if (serverVersion || loginServerVersion) {
                        if (!isOnline) {
                            if (serverVersion) {
                                this.store.dispatch(MobileActions.setOnline(NetworkStates.ONLINE));
                            } else {
                                // We don't have a server version, so server unreachable. However, if my0 is reachable, then we are not offline, the server is.
                                this.store.dispatch(MobileActions.setOnline(NetworkStates.SERVER_OFFLINE));
                            }
                        } else {
                            if (serverVersion) {
                                let sourceCodeVersion = this.store.getState().data.sourceCodeVersion;
                                if (!sourceCodeVersion || serverVersion !== sourceCodeVersion) {
                                    serviceWorker
                                        .update()
                                        .catch(() => {
                                            updateAppCache();
                                        });
                                }

                                if (previousStatus === NetworkStates.CLIENT_OFFLINE) {
                                    // We were down for a while, trigger a sync
                                    mainScheduler.fireAllWithMatchingKey("@sync");
                                }
                                this.store.dispatch(MobileActions.setOnline(NetworkStates.ONLINE));
                            } else {
                                // We don't have a server version, so server unreachable. However, if my0 is reachable, then we are not offline, the server is.
                                this.store.dispatch(MobileActions.setOnline(NetworkStates.SERVER_OFFLINE));
                            }
                        }
                    } else {
                        // We don't have either version, so we must be offline.
                        this.store.dispatch(MobileActions.setOnline(NetworkStates.CLIENT_OFFLINE));
                    }
                });
        }, constants.CHECK_NETWORK_GRACE_PERIOD_MS);
    }

    checkVehicleProximity() {
        return this.context.vehicleProximity.check();
    }

    private showPromptDriverFromLog(slimLog: DutyStatusLog, currentDriver: DeepReadonlyObject<IUser>, currentVehicle: IIdEntity, options?: IVehicleEndedStatusOptions): CancelablePromise {
        const slimLogDriverId = slimLog.driver && slimLog.driver.id;
        const collection = this.getFilteredLogCollection(slimLog, slimLogDriverId);

        if (!collection.length) {
            options?.onPromptHandled?.();
            this.logDriverLogPromptEvent("Prompt suppressed: no eligible logs in collection", slimLog);
            return CancelablePromise.resolve();
        }

        const latestLog = this.getLatestLog(slimLog, collection);

        if (this.promptPreconditionsFail(currentDriver, currentVehicle, slimLog, latestLog)) {
            options?.onPromptHandled?.();
            this.logDriverLogPromptEvent("Prompt suppressed: promptPreconditionsFail", slimLog);
            return CancelablePromise.resolve();
        }

        const logBeforePromptLog = this.dutyStatusLogService.getLatestLog(collection, slimLog);
        const promptDetails = this.evaluatePromptDetails(slimLog, logBeforePromptLog, collection, currentDriver, options);

        // PC_Exempted continues across ignition cycles — no prompt and no automatic exit.
        if ((promptDetails.isEndedPcPrompt || promptDetails.isEndedPcViaSdc) && isPcExemptedPeriod(logBeforePromptLog, collection, this.dutyStatusLogService)) {
            options?.onPromptHandled?.();
            this.logDriverLogPromptEvent("Prompt suppressed: PC_Exempted continues across ignition cycle", slimLog);
            return CancelablePromise.resolve();
        }

        const promptNotExpected = isThePromptNotExpected(
            logBeforePromptLog,
            promptDetails.newStatus as THosStatus,
            slimLogDriverId,
            promptDetails.isEndedPcPrompt || false,
            this.dutyStatusLogService
        );
        if (!promptDetails.shouldShowPrompt || promptNotExpected) {
            options?.onPromptHandled?.();
            this.logDriverLogPromptEvent(`Prompt suppressed: shouldShowPrompt=${ promptDetails.shouldShowPrompt }, promptNotExpected=${ promptNotExpected }`, slimLog);
            return CancelablePromise.resolve();
        }

        // Exemption permission was revoked — mirror "yes" behavior without showing the prompt.
        if (this.isExemptionPermissionRevoked(promptDetails.isEndedPcPrompt || promptDetails.isEndedPcViaSdc, promptDetails.isEndedYmPrompt, currentDriver)) {
            const revokedExemption = promptDetails.isEndedPcPrompt || promptDetails.isEndedPcViaSdc ? "personal conveyance" : "yard move";
            this.logDriverLogPromptEvent(`Prompt suppressed: ${ revokedExemption } exemption permission revoked (handled without prompt)`, slimLog);
            if (this.endedExemptionOnRevokeLogId === logBeforePromptLog?.id) {
                return CancelablePromise.resolve();
            }
            this.endedExemptionOnRevokeLogId = logBeforePromptLog?.id ?? null;
            try {
                if (promptDetails.isEndedPcViaSdc) {
                    this.handleSdcEndedPcPrompt(currentDriver as IUser, slimLog, promptDetails.newStatus, true);
                } else if (promptDetails.isEndedPcPrompt) {
                    this.handlePcPrompt(currentDriver as IUser, slimLog, promptDetails.newStatus, true, logBeforePromptLog, "OtherUser");
                } else {
                    // For YM, "yes" (ended=true) is a no-op — the system ON log already represents the end of YM,
                    // consistent with the user-facing "yes" path.
                    this.handleYmPrompt(slimLog, promptDetails.newStatus, true);
                }
            } catch (e) {
                this.endedExemptionOnRevokeLogId = null;
                handleApi(e);
            }
            options?.onPromptHandled?.();
            return CancelablePromise.resolve();
        }

        return this.showUserPromptDialog(
            currentDriver,
            slimLog,
            promptDetails,
            logBeforePromptLog,
            (dialog) => {
                options?.onPromptPresented?.();
                this.logDriverLogPromptEvent("Prompt opened: dialog presented", slimLog);
                applyDriverActionNecessary(
                    this.store,
                    webViewLayer.driverActionTypes.ConfirmHOSStatus,
                    options?.onPromptDismissed
                )(dialog);
            },
            options?.onPromptHandled
        )
            .catch((err: IApiError) => {
                this.errorHandler.handleApiImpl(err, true);
            });
    }

    private logDriverLogPromptEvent(message: string, slimLog?: DutyStatusLog): void {
        const context = slimLog ? ` (status: ${ slimLog.status }, logDate: ${ slimLog.getDate().toISOString() })` : "";
        logEvent(
            LoggerEvent.DRIVERLOGPROMPT,
            `${ message }${ context }`,
            this.userService.getUsersObject(),
            this.userService.getCurrentUser()
        );
    }

    private closeDriverLogPrompt(reason: string): CancelablePromise {
        if (removeDialog("user-log-prompt-dialog")) {
            this.logDriverLogPromptEvent(`Prompt closed: ${ reason }`);
        }
        return CancelablePromise.resolve();
    }

    private isExemptionPermissionRevoked(
        isEndedPcPrompt: boolean | undefined,
        isEndedYmPrompt: boolean | undefined,
        currentDriver: DeepReadonlyObject<IUser>
    ): boolean {
        return (!!isEndedPcPrompt && !(currentDriver.isPersonalConveyanceEnabled ?? true)) ||
            (!!isEndedYmPrompt && !(currentDriver.isYardMoveEnabled ?? true));
    }

    private resolvePromptDialogType(
        isEndedPcPrompt: boolean,
        isEndedYmPrompt: boolean,
        isStoppedDrivingPrompt: boolean
    ): { newStatus: THosStatus; dialogText: string; selfClosingTimeLimit?: number } | null {
        if (isEndedYmPrompt && isInCanada(this.hosUtils.getLatestHosRuleSetIdShortened())) {
            return { newStatus: "YM", dialogText: "ended yard move." };
        }
        if (isEndedPcPrompt) {
            return { newStatus: "PC", dialogText: "ended personal conveyance." };
        }
        if (isStoppedDrivingPrompt) {
            return { newStatus: "D", dialogText: "stopped driving.", selfClosingTimeLimit: constants.DIALOG.SELF_CLOSING_TIMEOUT_SECS };
        }
        return null;
    }

    private handleIoxPromptNo(
        isEndedPcPrompt: boolean,
        isEndedYmPrompt: boolean,
        dutyStatusLog: DutyStatusLog,
        newStatus: THosStatus,
        currentDriver: IUser,
        userPromptDialog: Dialog
    ) {
        userPromptDialog.remove();
        if (isEndedPcPrompt || dutyStatusLog._isTempIoxLog || isEndedYmPrompt) {
            this.editEngineCycleLogToManualLog(dutyStatusLog, newStatus);
        } else {
            let logDate = new Date(dutyStatusLog.getDate().toISOString());
            logDate.setSeconds(logDate.getSeconds() + 1);
            this.addManualLogAfterPromptLog(currentDriver, logDate, dutyStatusLog.device, "D");
        }
    }

    private logAllEvents(evtName: string, evtData: any) {
        if (window.DRIVE_DEBUG && window.DRIVE_DEBUG.LOG_EVENTS_TO_CONSOLE) {
            let eventInfo = ["EVENT", evtName];

            if (evtData !== null && typeof evtData !== "undefined") {
                eventInfo.push(evtData);
            }

            if (evtName.indexOf("logger:") !== -1) {
                // Don't log internal logging logs
                return;
            }

            log(Source.DEBUG, JSON.stringify(eventInfo), this.userService.getUsersObject());
        }
    }

    /**
     * This method (triggered by the dispatcher event "RemoveUserSessions" will manually remove user's sessions by making a call
     * to `session.clearStore`. If a list of users is provided, then we will do so for that list of users. Otherwise we will grab
     * a list of inactive users and perform this action for each of them. The call to `clearStore` removes all relevant data from
     * storage and removes all scheduled tasks, as well as their context.
     *
     * @param users Optionally, a list of users (usernames) who's sessions we want to end instead of all inactive users.
     */
    private removeUserSessions(users?: string[]) {
        let usersToRemove = (users && users.length ? users : this.userService.getInactiveUsers()).map(name => this.userService.getUserByName(name));

        if (usersToRemove && usersToRemove.length) {
            usersToRemove.forEach(user => this.session.clearStore((user as IUser), true, true));
        }
    }

    private handleTimeEnd(from: Source, id: string, taskName: string, user?: string | any, isSuccessful?: boolean): void {
        logTimeEnd(from, id, taskName, this.userService.getUsersObject(), user, isSuccessful);
    }

    private loggerFeedback(message: string, additionalDetails: AdditionalDetails) {
        feedback(message, this.userService.getUsersObject(), additionalDetails);
        this.sync.eventLogs.upload(true);
    }

    /**
     * Setup store observers
     */
    private setupStoreSubscribers() {
        this.observeStore(state => state.data.debugMode, this.handleDebugMode.bind(this));
        this.observeStore(state => state.data.nightMode, this.handleNightMode.bind(this));
        this.observeStore(state => state.mobile.charging, this.changeCharging.bind(this));
        this.observeStore(state => state.mobile.embed, this.setEmbeddedMode.bind(this));
        this.observeStore(state => state.mobile.background, this.backgroundChange.bind(this));
        this.observeStore(state => state.mobile.loginRequired, this.loginRequiredChange.bind(this));
        this.observeStore(state => state.mobile.driverActionNecessary, this.driveActionNecessaryChanged.bind(this));
        // Keep the native container's session list in sync with the WebView's
        // ordered active-user list. The first element is the user currently
        // viewing the app and becomes the cold-start restore target on the RN side.
        // Fires whenever the list changes: login, logout, or view-switch.
        // Use userService.getActiveUserNames() in the callback to get a properly
        // typed string[] without needing to annotate the DeepReadonly wrapper type.
        this.observeStore(state => state.activeUsers, () => webViewLayer.syncActiveUsers(this.userService.getActiveUserNames()));
        // Emit the current list immediately on startup so the native container is
        // in sync from the first frame — observeStore only fires on changes, so
        // without this the native side would miss any users already in the store
        // (e.g. restored from a previous session before the first Redux update).
        webViewLayer.syncActiveUsers(this.userService.getActiveUserNames());
        this.observeStore(state => state.data.driving, () => this.store.getState().data.driving && this.promptDriverFromLog());
        this.observeStore(state => state.mobile.pageState, this.handlePageStateUpdate.bind(this));
        this.observeStore(
            state => [state.data.featureFlags[FeatureFlagName.ENABLE_SENTRY_TRACING], state.data.serverFlags] as [IFeatureFlagStatus, number],
            async () => {
                await this.errorTracker?.configureTracingIntegration(
                    isAllowThirdPartyLoggingEnabled(this.store.getState().data.serverFlags),
                    this.featureFlagService.hasFeatureFlag(FeatureFlagName.ENABLE_SENTRY_TRACING)
                );
            }
        );
        this.featureFlagService.observeFlagChanges(FeatureFlagName.PIP_FUNCTIONALITY, flagStatus => {
            if (flagStatus) {
                setPipMode(flagStatus.isEnabled && this.store.getState().data.pipMode);
            }
        });

        // Handle translation storage mode changes - clear the inactive storage when switching
        const onTranslationReduxStoreFlagChange = (flagStatus: IFeatureFlagStatus | undefined) => {
            if (flagStatus?.isEnabled) {
                // Killswitch activated: clear the Redux translation store
                this.store.dispatch(TranslationActions.clearTranslations({}));
            } else {
                // Killswitch deactivated: clear any stale localStorage translations that may have been
                // written before the flag was loaded
                this.translationService.getTranslator().validateCache(this.translationService.getLanguage());
            }
        };
        this.featureFlagService.observeFlagChanges(FeatureFlagName.DISABLE_TRANSLATION_REDUX_STORE, onTranslationReduxStoreFlagChange);

        this.driveNativeStatePublisher.setPublishingEnabled(
            !this.featureFlagService.hasFeatureFlag(FeatureFlagName.DISABLE_DRIVE_NATIVE_STATE_SYNC)
        );
        this.driveNativeStatePublisher.start();
        this.featureFlagService.observeFlagChanges(
            FeatureFlagName.DISABLE_DRIVE_NATIVE_STATE_SYNC,
            flagStatus => this.driveNativeStatePublisher.setPublishingEnabled(!flagStatus.isEnabled)
        );

    }
    private onContextRemoved(userName: string) {
        if (this.context.user === userName || this.context.user === null) {
            // The current user's context was removed
            // Use the next active user's context
            this.context = this.manager.getContext();
        }
    }

    private refreshDstCalculator() {
        let users = this.userService.getUsersObject();
        let usersKey = this.context.user;

        if (usersKey && users.hasOwnProperty(usersKey.toLowerCase())) {
            let timeZoneId = users[usersKey.toLowerCase()].timeZoneId;
            timeZoneId && changeTimeZoneForDstCalculator(timeZoneId, this.userService.getUsersTimezoneInfo());
        }
    }

    private onUserLanguageUpdate(user: IUser): void {
        let currentUser = this.userService.getCurrentUser();
        currentUser && user && currentUser.name === user.name && this.render();
    }

    private onScroll(e: Event) {
        const target = e.target as HTMLElement;
        if (target.scrollTop > SCROLL_MINIMUM && !this.isScrollBtnVisible) {
            this.isScrollBtnVisible = true;
            this.toggleButton();
        } else if (target.scrollTop < SCROLL_MINIMUM && this.isScrollBtnVisible) {
            this.isScrollBtnVisible = false;
            this.toggleButton();
        }
    }

    private setScrollListener(enable: boolean) {
        let scrollElement = document.getElementById("scroll-content");
        let buttonElement = document.getElementById("scroll-top-button");
        if (scrollElement && buttonElement) {
            if (enable) {
                show(buttonElement);
                scrollElement.addEventListener("scroll", this.onScrollBind, false);
            } else {
                hide(buttonElement);
                scrollElement.removeEventListener("scroll", this.onScrollBind, false);
            }
            buttonElement.classList.remove("show");
        }
    }

    private scrollTop() {
        let scrollElement = document.getElementById("scroll-content");
        if (scrollElement && this.isScrollBtnVisible) {
            scrollElement.scrollTop = 0;
        }
    }

    private handleDebugMode(debugMode: boolean) {
        const button = document.getElementById("debug-shortcut-button");
        if (button) {
            button.classList.toggle("show", debugMode);
        }
    }

    private navigateToDebugMain() {
        this.router.navigate("debug/main", true);
    }

    private onNewIoxUsbDeviceAttached(sameIoxUsbDeviceAttached: boolean, newDeviceId?: string, newSerialNo?: string) {
        if (!sameIoxUsbDeviceAttached) {
            let driverSeatId = this.userService.getDriversSeatUser()?.id;
            let currDriverId = this.userService.getCurrentUser()?.id;
            let currDeviceId = getCurrentVehicle()?.id;

            let isDifferentDevice = newDeviceId && newDeviceId !== currDeviceId;
            if (currDeviceId && !newDeviceId && newSerialNo) {
                const deviceCache = makeDeviceCache(this.store);
                const populatedDevice = deviceCache.get({ id: currDeviceId })[0];

                if (populatedDevice && populatedDevice.serialNumber) {
                    isDifferentDevice = populatedDevice.serialNumber !== newSerialNo;
                    if (!isDifferentDevice) {
                        this.store.dispatch(DataActions.setCurrentHardwareId(newSerialNo));
                    } else {
                        this.context.translationService.getTranslator().translate(["Device could not be attached due to network issues."])
                        .then(([translatedErrorMessage]) => toastr.error(
                            translatedErrorMessage,
                            undefined,
                            undefined,
                            { isUserNightMode: this.store.getState().data.nightMode }
                        ));
                    }
                }
            }

            if (!currDeviceId || isDifferentDevice) {
                if (driverSeatId !== currDriverId) {
                    this.router.navigate(`login,(switchUser:${ driverSeatId },${ (newDeviceId ? `newDeviceId:${ newDeviceId },` : "") }reselectVehicle:true)`, true);
                } else {
                    let nextPageState: IWorkflowState = {};
                    if (newDeviceId) {
                        nextPageState.newDeviceId = newDeviceId;
                    }
                    this.router.toWorkflow(false, nextPageState);
                }
            }
        }
    }

    private loginRequiredChange(loginRequired: ILoginRequiredState) {
        webViewLayer.loginRequired(loginRequired);
    }

    private onImageAdded(fileObj: {filePath: string; cameraIconId: string}) {
        let newImageViewDialog = new PictureViewDialog(this.context.translationService.getTranslator(), this.store, fileObj.filePath, fileObj.cameraIconId);
        void newImageViewDialog.open();
    }

    private handlePageStateUpdate(pageState: IMobilePageState) {
        this.handleNightMode();
        if (pageState.title) {
            let getFormattedPageTitle = (title: string) => `${ this.basePageTitle } - ${ title }`;
            let newPageTitle = getFormattedPageTitle(pageState.title);

            // Update title if it's changed
            if (document.title !== newPageTitle) {
                document.title = newPageTitle;
            }

            return;
        }

        // Fallback
        document.title = this.basePageTitle;
    }

    private handleNightMode() {
        const isLytx = window.whitelabel === "lytx";
        const nightMode = this.store.getState().data.nightMode;

        // When exiting inspection mode, location hash may have changed but page hasn't (due to dialog), don't want to render nightmode prematurely
        const blockNightMode = !!document.querySelector("div#inspection-container") || isDriverViewingInspectionMode();

        if ((isLytx || nightMode) && !blockNightMode && !this.el.classList.contains("nightMode")) {
            this.el.classList.add("nightMode");
        } else if (((!isLytx && !nightMode) || blockNightMode) && this.el.classList.contains("nightMode")) {
            this.el.classList.remove("nightMode");
        }
    }


    private driveActionNecessaryChanged(driverActionNecessaryState?: IDriverActionNecessary) {
        try {
            webViewLayer.driverActionNecessary(driverActionNecessaryState?.isNecessary, driverActionNecessaryState?.actionType);
        } catch (e) {
            logError(Source.ERROR, `webViewLayer Error: ${ e }`, this.userService.getUsersObject());
        }
    }

    private backgroundChange(background?: boolean): Promise<Dialog | undefined> {
        let state = this.store.getState();
        let currentUser = this.userService.getCurrentUser();

        // If back in foreground and clocked out, ask if they want to clock in
        // Do not interrupt an in-progress inspection (driver would lose their work)
        if (this.securityService.can("viewDriverClockInOut") && background === false && currentUser &&
            state.data.workday[currentUser.id] === WorkdayType.Stopped && !isDriverViewingInspectionMode()) {
            let workdayForegroundDialog = new Dialog(
                "workday-foreground-dialog",
                `Do you want to clock in?`,
                this.context.translationService.getTranslator(),
                `Welcome back!`,
                [[
                    {
                        id: "clock-in-now",
                        icon: "drive-clock",
                        text: "Yes",
                        callback: () => {
                            let nextPageState: IWorkflowState = {};
                            let driver = this.userService.getDriversSeatUser();
                            if (currentUser && driver && driver.id !== currentUser.id) {
                                nextPageState = { coDriver: true };
                            }
                            workdayForegroundDialog && workdayForegroundDialog.remove();
                            this.router.toWorkflow(false, nextPageState);
                        }
                    },
                    {
                        id: "clock-in-later",
                        icon: "icon-coffee",
                        text: "Don't clock in",
                        className: "button_secondary",
                        callback: () => {
                            workdayForegroundDialog && workdayForegroundDialog.remove();
                        }
                    }
                ]],
                {
                    isSelfClosing: 20,
                    isNightMode: this.store.getState().data.nightMode
                }
            );

            return workdayForegroundDialog.open()
                .then(applyDriverActionNecessary(this.store, webViewLayer.driverActionTypes.Workflow));
        }
        return CancelablePromise.resolve();
    }

    private hideToastMessage() {
        let existingToasts = document.querySelectorAll(`[data-origin="${ ToastOrigins.HOS_STATUS_CHANGE }"]`);
        Array.prototype.filter.call(existingToasts, (toast: HTMLElement) => toastr.remove(toast));
    }

    private updateCachingMethod() {
        if (!("serviceWorker" in navigator)) {
            return;
        }

        let unregisteredCurrentServiceWorker = false;
        return navigator.serviceWorker.getRegistrations()
            .then((registrations) => {
                let registrationPromises: Promise<boolean>[] = [];
                for (let registration of registrations) {
                    let registrationUrl = (registration.active?.scriptURL || "").toLowerCase();
                    // drive/build/service-worker is used by release 9.0 and above, drive/service-worker is used by release 8.0 and below
                    if (registrationUrl && (registrationUrl.indexOf("drive/build/service-worker") > -1 || registrationUrl.indexOf("drive/service-worker"))) {
                        if (!window.serviceWorkerEnabled) {
                            registrationPromises.push(registration.unregister() as CancelablePromise);
                            unregisteredCurrentServiceWorker = true;
                        }
                    }
                }
                return Promise.all(registrationPromises);
            })
            .then(() => caches.keys())
            .then(keys => {
                if (window.serviceWorkerEnabled) {
                    return CancelablePromise.resolve();
                }

                return CancelablePromise.all(keys.map(key => {
                    if (key.indexOf("drive-cache-") === 0) {
                        return caches.delete(key);
                    }
                    return CancelablePromise.resolve();
                }));
            })
            .then(() => {
                loggerLog(Source.SERVWORKER, `AppView.updateCachingMethod ${ unregisteredCurrentServiceWorker }`);
                if (unregisteredCurrentServiceWorker) {
                    window.location.reload();
                }
            })
            .catch(handleApi);
    }

    private logServiceWorkerPerformance() {
        try {
            window.performance?.getEntriesByType && window.performance.getEntriesByType("measure").forEach(perf => {
                if (perf.name.indexOf("ServiceWorker") === -1) {
                    return;
                }
                logEvent(
                    LoggerEvent.PERFORMANCE,
                    JSON.stringify({
                        name: perf.name,
                        duration: perf.duration,
                        startTime: perf.startTime,
                        entryType: perf.entryType
                    }),
                    this.userService.getUsersObject(),
                    this.userService.getCurrentUser(),
                    perf.duration
                );
                window.performance.clearMeasures(perf.name);
            });
        } catch {}
    }
}
